import { lazy, Suspense } from 'react'
import type { ReactNode } from 'react'

import { createBrowserRouter, Outlet } from 'react-router-dom'

import { FullscreenLoader } from '@/components/common/FullscreenLoader'
import PwaTracker from '@/components/common/PwaTracker'
import ReferralTracker from '@/components/common/ReferralTracker'
import BottomNav from '@/components/layout/BottomNav'

const Home = lazy(() => import('@/pages/Home'))
const Activity = lazy(() => import('@/pages/Activity'))
const Market = lazy(() => import('@/pages/Market'))
const MarketGuide = lazy(() => import('@/pages/Market/Guide'))
const MarketOrders = lazy(() => import('@/pages/Market/Orders'))
const MarketPaymentSuccess = lazy(() => import('@/pages/Market/PaymentSuccess'))
const Team = lazy(() => import('@/pages/Team'))
const Assets = lazy(() => import('@/pages/Assets'))
const NotFound = lazy(() => import('@/pages/NotFound'))
const Login = lazy(() => import('@/pages/Login'))
const LoginVerify = lazy(() => import('@/pages/Login/Verify'))
const LoginResetPassword = lazy(() => import('@/pages/Login/ResetPassword'))
const Profile = lazy(() => import('@/pages/Profile'))
const Messages = lazy(() => import('@/pages/Messages'))
const MessageDetail = lazy(() => import('@/pages/Messages/Detail'))
const FarmDetail = lazy(() => import('@/pages/Farm'))
const AssetDetail = lazy(() => import('@/pages/Assets/Detail'))
const Discover = lazy(() => import('@/pages/Discover'))
const Install = lazy(() => import('@/pages/Install'))
const Map = lazy(() => import('@/pages/Map'))
const QA = lazy(() => import('@/pages/QA'))
const Withdraw = lazy(() => import('@/pages/Withdraw'))
const WithdrawHistory = lazy(() => import('@/pages/Withdraw/History'))
const ActivityHistory = lazy(() => import('@/pages/Activity/History'))
const ActivitySubmit = lazy(() => import('@/pages/Activity/Submit'))

function LazyLoad({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullscreenLoader />}>{children}</Suspense>
}

/**
 * 根布局：承载底部导航（需要 Router 上下文），通过 <Outlet /> 渲染具体页面。
 */
function RootLayout() {
  return (
    <>
      <ReferralTracker />
      <PwaTracker />
      <Outlet />
      <BottomNav />
    </>
  )
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: (
          <LazyLoad>
            <Home />
          </LazyLoad>
        ),
      },
      {
        path: '/activity',
        element: (
          <LazyLoad>
            <Activity />
          </LazyLoad>
        ),
      },
      {
        path: '/market',
        element: (
          <LazyLoad>
            <Market />
          </LazyLoad>
        ),
      },
      {
        path: '/market/guide',
        element: (
          <LazyLoad>
            <MarketGuide />
          </LazyLoad>
        ),
      },
      {
        path: '/market/orders',
        element: (
          <LazyLoad>
            <MarketOrders />
          </LazyLoad>
        ),
      },
      {
        path: '/market/payment-success',
        element: (
          <LazyLoad>
            <MarketPaymentSuccess />
          </LazyLoad>
        ),
      },
      {
        path: '/team',
        element: (
          <LazyLoad>
            <Team />
          </LazyLoad>
        ),
      },
      {
        path: '/assets',
        element: (
          <LazyLoad>
            <Assets />
          </LazyLoad>
        ),
      },
      {
        path: '/assets/:symbol',
        element: (
          <LazyLoad>
            <AssetDetail />
          </LazyLoad>
        ),
      },
      {
        path: '/withdraw',
        element: (
          <LazyLoad>
            <Withdraw />
          </LazyLoad>
        ),
      },
      {
        path: '/withdraw/history',
        element: (
          <LazyLoad>
            <WithdrawHistory />
          </LazyLoad>
        ),
      },
      {
        path: '/activity/history',
        element: (
          <LazyLoad>
            <ActivityHistory />
          </LazyLoad>
        ),
      },
      {
        path: '/activity/submit',
        element: (
          <LazyLoad>
            <ActivitySubmit />
          </LazyLoad>
        ),
      },
      {
        path: '/profile',
        element: (
          <LazyLoad>
            <Profile />
          </LazyLoad>
        ),
      },
      {
        path: '/messages',
        element: (
          <LazyLoad>
            <Messages />
          </LazyLoad>
        ),
      },
      {
        path: '/messages/:id',
        element: (
          <LazyLoad>
            <MessageDetail />
          </LazyLoad>
        ),
      },
      {
        path: '/farm/:id',
        element: (
          <LazyLoad>
            <FarmDetail />
          </LazyLoad>
        ),
      },
      {
        path: '/login',
        element: (
          <LazyLoad>
            <Login />
          </LazyLoad>
        ),
      },
      {
        path: '/login/verify',
        element: (
          <LazyLoad>
            <LoginVerify />
          </LazyLoad>
        ),
      },
      {
        path: '/login/reset-password',
        element: (
          <LazyLoad>
            <LoginResetPassword />
          </LazyLoad>
        ),
      },
      {
        path: '/discover',
        element: (
          <LazyLoad>
            <Discover />
          </LazyLoad>
        ),
      },
      {
        path: '/install',
        element: (
          <LazyLoad>
            <Install />
          </LazyLoad>
        ),
      },
      {
        path: '/map',
        element: (
          <LazyLoad>
            <Map />
          </LazyLoad>
        ),
      },
      {
        path: '/qa',
        element: (
          <LazyLoad>
            <QA />
          </LazyLoad>
        ),
      },
      {
        path: '*',
        element: (
          <LazyLoad>
            <NotFound />
          </LazyLoad>
        ),
      },
    ],
  },
])

export default router
